import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import SendtooLogo from "@/components/shared/SendtooLogo";
import {
  LayoutDashboard, Package, Wallet, User, LogOut, Menu, X,
  Users, Settings, BarChart3, MapPin, ChevronRight, Truck, Warehouse,
  Scale, FileText, Layers, CreditCard, ChevronDown,
  TrendingUp, Upload, Plus, RefreshCw,
  AlertTriangle, MessageCircle, LifeBuoy, Clock, ShieldCheck
} from "lucide-react";

const customerNav = [
  { to: "/customer", label: "Dashboard", icon: LayoutDashboard },
  { to: "/customer/shipments", label: "My Shipments", icon: Package },
  { to: "/customer/book", label: "Book Shipment", icon: Truck },
  { to: "/customer/pickup-requests", label: "Pickup Requests", icon: Truck },
  { to: "/customer/wallet", label: "Wallet", icon: Wallet },
  { to: "/customer/invoices", label: "Invoices", icon: FileText },
  { to: "/customer/rate-calculator", label: "Rate Calculator", icon: BarChart3 },
  { to: "/customer/disputes", label: "My Disputes", icon: AlertTriangle },
  { to: "/customer/support", label: "Support Tickets", icon: LifeBuoy },
  { to: "/customer/insurance-claims", label: "Insurance Claims", icon: ShieldCheck },
  { to: "/customer/profile", label: "Profile", icon: User },
];

const agentNav = [
  { to: "/staff", label: "Dashboard", icon: LayoutDashboard },
  { to: "/staff/book", label: "New Booking", icon: Truck },
  { to: "/staff/orders", label: "My Orders", icon: Package },
  { to: "/staff/assignment", label: "Assignment Console", icon: Layers },
  { to: "/staff/pickup-console", label: "Pickup Console", icon: MapPin },
  { to: "/staff/bulk-upload", label: "Bulk Upload", icon: Upload },
  { to: "/staff/customers", label: "Customers", icon: Users },
  { to: "/staff/revenue", label: "Revenue", icon: BarChart3 },
  { to: "/staff/network-assignments", label: "By Network", icon: Layers },
  { to: "/staff/rate-calculator", label: "Rate Calculator", icon: BarChart3 },
  { to: "/staff/ndr", label: "NDR Console", icon: AlertTriangle },
  { to: "/staff/support", label: "Support Tickets", icon: LifeBuoy },
];

const adminNav = [
  // Ungrouped
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/book", label: "New Booking", icon: Truck },

  {
    label: "Shipments",
    icon: Package,
    children: [
      { to: "/admin/shipments", label: "All Shipments", icon: Package },
      { to: "/staff/pickup-console", label: "Pickup Console", icon: MapPin },
      { to: "/admin/bulk-upload", label: "Bulk Upload", icon: Upload },
    ],
  },
  {
    label: "Operations",
    icon: AlertTriangle,
    children: [
      { to: "/admin/ops-overview", label: "Ops Overview", icon: LayoutDashboard },
      { to: "/admin/assignment", label: "Assignment Console", icon: Layers },
      { to: "/admin/network-assignments", label: "By Network", icon: Layers },
      { to: "/admin/ndr", label: "NDR Console", icon: AlertTriangle },
      { to: "/admin/disputes", label: "Disputes", icon: MessageCircle },
      { to: "/admin/support", label: "Support Tickets", icon: LifeBuoy },
      { to: "/admin/demurrage", label: "Demurrage Queue", icon: Clock },
      { to: "/admin/insurance-claims", label: "Insurance Claims", icon: ShieldCheck },
      { to: "/admin/reconciliation-v2", label: "Reconciliation", icon: Scale },
    ],
  },
  {
    label: "Customers & Partners",
    icon: Users,
    children: [
      { to: "/admin/customers", label: "Customers", icon: Users },
      { to: "/admin/partners", label: "Partners", icon: Truck },
      { to: "/admin/staff", label: "Staff", icon: ShieldCheck },
    ],
  },
  {
    label: "Finance",
    icon: CreditCard,
    children: [
      { to: "/admin/revenue", label: "Revenue", icon: BarChart3 },
      { to: "/admin/invoices", label: "Invoices", icon: FileText },
      { to: "/admin/combine-invoices", label: "Combine Invoices", icon: FileText },
      { to: "/admin/monthly-invoice-drafts", label: "Monthly Invoice Drafts", icon: FileText },
      { to: "/admin/wallet-dashboard", label: "Wallet", icon: Wallet },
      { to: "/admin/wallet-ledger-v2", label: "Wallet Ledger", icon: Wallet },
      { to: "/admin/rate-agreements", label: "Rate Agreements", icon: FileText },
    ],
  },
  {
    label: "Configuration",
    icon: Settings,
    children: [
      { to: "/admin/rate-engine", label: "Rate Engine", icon: Settings },
      { to: "/admin/config", label: "Admin Config", icon: Settings },
      { to: "/admin/oda-pincodes", label: "ODA Pincodes", icon: MapPin },
      { to: "/admin/warehouses", label: "Warehouses", icon: Warehouse },
      { to: "/admin/security", label: "Security (2FA)", icon: ShieldCheck },
    ],
  },
  {
    label: "Reports",
    icon: BarChart3,
    children: [
      { to: "/admin/reports", label: "Reports", icon: BarChart3 },
      { to: "/admin/reconciliation", label: "Reconciliation", icon: Scale },
    ],
  },
];

function getNavLinks(role) {
  if (role === "admin") return adminNav;
  if (role === "agent" || role === "staff") return agentNav;
  return customerNav;
}

function getPartnerNav(partnerType) {
  const t = (partnerType || "NETWORK").toUpperCase();
  const items = [
    { to: "/partner", label: "Dashboard", icon: LayoutDashboard },
  ];
  if (t === "NETWORK" || t === "REVENUE") {
    items.push({ to: "/partner/bookings", label: "New Booking", icon: Plus });
  }
  items.push({ to: "/partner/shipments", label: "My Shipments", icon: Package });
  items.push({ to: "/partner/earnings", label: "My Earnings", icon: TrendingUp });
  if (t === "NETWORK" || t === "EXECUTION") {
    items.push({ to: "/partner/upload-proof", label: "Upload Proof", icon: Upload });
  }
  return items;
}

function NavGroup({ group, currentPath, onNavigate }) {
  const childPaths = group.children.map((c) => c.to);
  const isChildActive = childPaths.some((p) => currentPath === p || currentPath.startsWith(p + "/"));
  const [open, setOpen] = useState(isChildActive);
  const GroupIcon = group.icon;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-group-${group.label.toLowerCase()}`}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all min-h-[44px] ${
          isChildActive ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
        }`}
      >
        <GroupIcon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
          {group.children.map((c) => {
            const active = currentPath === c.to || currentPath.startsWith(c.to + "/");
            const ChildIcon = c.icon;
            return (
              <Link
                key={`${c.to}-${c.label}`}
                to={c.to}
                data-testid={`nav-${c.label.toLowerCase().replace(/ /g, "-")}`}
                onClick={onNavigate}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all min-h-[40px] ${
                  active ? "bg-white/15 text-white" : "text-slate-400 hover:bg-white/10 hover:text-white"
                }`}
              >
                <ChildIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                <span className="flex-1">{c.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Single "Actions ▾" dropdown — role-based items, opens on hover and click
const ACTIONS_BY_ROLE = {
  admin: [
    { icon: "📦", label: "New Booking", to: "/admin/book", primary: true },
    { divider: true },
    { icon: "🔀", label: "Assign", to: "/staff/assignment" },
    { icon: "🚚", label: "Pickups", to: "/staff/pickup-console" },
    { icon: "📊", label: "Rates", to: "/admin/rate-engine" },
    { icon: "📈", label: "Insights", to: "/admin/ops-overview" },
  ],
  staff: [
    { icon: "📦", label: "New Booking", to: "/staff/book", primary: true },
    { divider: true },
    { icon: "🔀", label: "Assign", to: "/staff/assignment" },
    { icon: "🚚", label: "Pickups", to: "/staff/pickup-console" },
  ],
  partner: [
    { icon: "📦", label: "New Booking", to: "/staff/book", primary: true },
  ],
  agent: [
    { icon: "📦", label: "New Booking", to: "/agent/book", primary: true },
  ],
};

function ActionsDropdown({ user }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hoverTimer = useRef(null);
  const role = user?.role;
  const items = ACTIONS_BY_ROLE[role] || [];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleEnter = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpen(false), 120);
  };

  if (role === "customer" || items.length === 0) return null;

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-testid="header-actions"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="header-actions-btn"
        style={{
          backgroundColor: "#1a1a2e",
          color: "#fff",
          borderRadius: "999px",
          fontSize: "13px",
          fontWeight: 500,
          padding: "8px 18px",
          border: "none",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        Actions <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        data-testid="header-actions-dropdown"
        style={{
          position: "absolute",
          right: 0,
          top: "calc(100% + 6px)",
          minWidth: "200px",
          background: "#fff",
          border: "0.5px solid #e2e8f0",
          borderRadius: "10px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0)" : "translateY(-4px)",
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 150ms ease, transform 150ms ease",
          overflow: "hidden",
          zIndex: 60,
        }}
      >
        {items.map((it, idx) => {
          if (it.divider) {
            return (
              <div
                key={idx}
                style={{ height: "1px", background: "#f1f5f9", margin: "2px 0" }}
              />
            );
          }
          return (
            <Link
              key={idx}
              to={it.to}
              onClick={() => setOpen(false)}
              data-testid={`header-action-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="actions-dd-item"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                height: "38px",
                padding: "0 16px",
                fontSize: "13px",
                fontWeight: it.primary ? 500 : 400,
                color: "#1e293b",
                textDecoration: "none",
                borderLeft: it.primary ? "3px solid #1D9E75" : "3px solid transparent",
              }}
            >
              <span>{it.icon}</span>
              <span>{it.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function DashboardLayout({ children, portalColor }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navLinks = user?.role === "partner"
    ? getPartnerNav(user?.partner_type)
    : getNavLinks(user?.role);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const roleLabel = { customer: "Customer Portal", agent: "Staff Portal", staff: "Staff Portal", admin: "Admin Panel", partner: "Partner Portal" }[user?.role] || "Portal";
  const roleColor = portalColor === "green"
    ? "bg-emerald-600"
    : { customer: "bg-blue-600", agent: "bg-orange-500", staff: "bg-orange-500", admin: "bg-purple-700", partner: "bg-emerald-600" }[user?.role] || "bg-brand-500";

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-700">
        <img src="/assets/sendtoo-logo-white.png" alt="Sendtoo" className="w-full max-w-[180px] h-auto" />
      </div>

      {/* Role badge */}
      <div className="px-6 py-3">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-white ${roleColor}`}>
          {roleLabel}
        </span>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
        {navLinks.map((link, idx) => {
          if (link.children) {
            return (
              <NavGroup
                key={`group-${link.label}-${idx}`}
                group={link}
                currentPath={location.pathname}
                onNavigate={() => setSidebarOpen(false)}
              />
            );
          }
          const isActive = location.pathname === link.to || (link.to !== "/customer" && link.to !== "/agent" && link.to !== "/admin" && link.to !== "/partner" && location.pathname.startsWith(link.to));
          return (
            <Link
              key={`${link.to}-${link.label}`}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all min-h-[44px] ${
                isActive
                  ? "bg-white/15 text-white"
                  : "text-slate-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              <link.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
              <span className="flex-1">{link.label}</span>
              {isActive && <ChevronRight className="w-4 h-4 opacity-70" />}
            </Link>
          );
        })}
      </nav>

      {/* User info + logout */}
      <div className="border-t border-slate-700 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-red-500/20 rounded-md transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 font-body overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-60 flex-col bg-[#0A183D] flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#0A183D] flex flex-col z-10">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center gap-4 flex-shrink-0">
          <button
            className="md:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-slate-800 hidden sm:block">{roleLabel}</h2>
          </div>
          {/* Actions dropdown — single button per role */}
          <div className="hidden md:flex items-center">
            <ActionsDropdown user={user} />
          </div>
          {user?.role === "customer" && (
            <div className="flex items-center gap-1.5 bg-brand-50 text-brand-500 px-3 py-1.5 rounded-md text-sm font-semibold">
              <Wallet className="w-4 h-4" />
              ₹{(user?.wallet_balance || 0).toFixed(2)}
            </div>
          )}
          <Link to="/" className="md:hidden">
            <SendtooLogo size="md" />
          </Link>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
